import type { ServiceCategoryRow } from "../../types";

export interface ServiceCategorySyncPlan {
  deletes: string[];
  creates: {
    clientKey: string;
    name: string;
    code: string;
    position: number;
  }[];
  updates: { id: string; name: string; code: string }[];
  reorders: { id: string; position: number }[];
}

// Pure diff of the editor's current rows against the server snapshot. Positions
// are 1-based and computed over the live (non-deleted) rows in display order.
export const planServiceCategorySync = (
  initial: ServiceCategoryRow[],
  current: ServiceCategoryRow[]
): ServiceCategorySyncPlan => {
  const initialById = new Map<
    string,
    { name: string; code: string; index: number }
  >();
  initial.forEach((r, index) => {
    if (r.id) initialById.set(r.id, { name: r.name, code: r.code, index });
  });

  const live = current.filter((r) => !r._deleted);

  const deletes = current
    .filter((r) => r._deleted && r.id)
    .map((r) => r.id as string);

  const creates: ServiceCategorySyncPlan["creates"] = [];
  const updates: ServiceCategorySyncPlan["updates"] = [];
  const reorders: ServiceCategorySyncPlan["reorders"] = [];

  live.forEach((row, i) => {
    const position = i + 1;
    if (!row.id) {
      creates.push({
        clientKey: row.clientKey,
        name: row.name,
        code: row.code,
        position,
      });
      return;
    }
    const prev = initialById.get(row.id);
    if (!prev) return; // unknown id — skip defensively
    if (prev.name !== row.name || prev.code !== row.code) {
      updates.push({ id: row.id, name: row.name, code: row.code });
    }
    if (prev.index !== i) {
      reorders.push({ id: row.id, position });
    }
  });

  return { deletes, creates, updates, reorders };
};
